'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var nthHookLoma = require('@narvar/nth-hook-loma');
var SessionContext = require('../../../../common/providers/SessionContext.js');
var index = require('../../../../common/providers/ClaimBuilder/index.js');
var useTitanAnalytics = require('../../../../common/providers/titan/useTitanAnalytics.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const ClaimDetailsContext = /*#__PURE__*/React__default["default"].createContext(null);
const useClaimDetails = () => {
  const context = React.useContext(ClaimDetailsContext);
  if (!context) {
    throw new Error('No context provided for ClaimDetails provider');
  }
  return context;
};
const ClaimDetailsProvider = _ref => {
  var _data$claimAgent, _data$claimAgent$clai, _data$claimAgent2, _updateClaimData$upda;
  let {
    children,
    value: {
      id,
      claimsRecommendationOverturnReasons
    }
  } = _ref;
  const titan = useTitanAnalytics["default"]();
  const {
    orderNumber: sessionOrderNumber,
    retailerMoniker,
    localeCountry,
    localeLanguage,
    timezone,
    setOrderNumber
  } = SessionContext.useSession();
  const [updateClaimSuccess, setUpdateClaimSuccess] = React__default["default"].useState(false);
  const {
    loading: claimDetailsLoading,
    data,
    error: claimDetailsError
  } = nthHookLoma.useGetClaimAgentQuery({
    variables: {
      retailerMoniker,
      id,
      localeCountry,
      localeLanguage,
      timezone
    }
  });
  const [updateClaim, {
    loading: updateClaimLoading,
    error: updateClaimError,
    data: updateClaimData
  }] = nthHookLoma.useUpdateClaimAgentMutation({
    update(cache, _ref2) {
      var _data$updateClaimAgen;
      let {
        data
      } = _ref2;
      const claim = data === null || data === void 0 ? void 0 : (_data$updateClaimAgen = data.updateClaimAgent) === null || _data$updateClaimAgen === void 0 ? void 0 : _data$updateClaimAgen.claim;
      if (!claim) return;
      cache.modify({
        id: `Claim:${claim.id}`,
        fields: {
          updatedOn() {
            return claim.updatedOn;
          },
          status() {
            return claim.status;
          },
          resolution() {
            return claim.resolution;
          }
        }
      });
    }
  });

  // In the event we navigate to this view without an order number already in context,
  // use the order number present in the claim
  const orderNumber = sessionOrderNumber || (data === null || data === void 0 ? void 0 : (_data$claimAgent = data.claimAgent) === null || _data$claimAgent === void 0 ? void 0 : (_data$claimAgent$clai = _data$claimAgent.claim) === null || _data$claimAgent$clai === void 0 ? void 0 : _data$claimAgent$clai.orderNumber);
  React.useEffect(() => {
    // Set order number in session if not already there
    if (!sessionOrderNumber && orderNumber) {
      setOrderNumber(orderNumber);
    }
  }, [orderNumber, sessionOrderNumber, setOrderNumber]);
  let claim;
  if (data !== null && data !== void 0 && (_data$claimAgent2 = data.claimAgent) !== null && _data$claimAgent2 !== void 0 && _data$claimAgent2.claim) {
    var _data$claimAgent$clai2;
    // Merge the items into the shipments
    const shipmentItems = (data.claimAgent.claim.items || []).reduce((acc, item) => {
      if (!item.trackingNumber) return acc;
      const existingArray = acc[item.trackingNumber];
      if (existingArray) {
        existingArray.push(item);
        return acc;
      }
      acc[item.trackingNumber] = [item];
      return acc;
    }, {});
    const shipments = (data.claimAgent.claim.shipments || []).map(shipment => ({
      ...shipment,
      items: shipmentItems[shipment.trackingNumber ?? ''] || []
    }));

    // Comments are always stored on items. If we have a package level claim,
    // all items in the package will have the same comment. Use the first item's comment
    // as the comment for the package.
    const customerComments = index.isItemClaimType(data.claimAgent.claim.claimType) ? (_data$claimAgent$clai2 = data.claimAgent.claim.items) === null || _data$claimAgent$clai2 === void 0 ? void 0 : _data$claimAgent$clai2.map(item => item.comments) : shipments.map(_ref3 => {
      let {
        items
      } = _ref3;
      return items[0].comments;
    });
    claim = {
      ...data.claimAgent.claim,
      shipments,
      customerComments: customerComments === null || customerComments === void 0 ? void 0 : customerComments.filter(comments => !!comments)
    };
  }
  const overturnReasonOptionsForAction = action => action === 'Deny' && (claimsRecommendationOverturnReasons === null || claimsRecommendationOverturnReasons === void 0 ? void 0 : claimsRecommendationOverturnReasons.Deny) || action === 'Cancel' && (claimsRecommendationOverturnReasons === null || claimsRecommendationOverturnReasons === void 0 ? void 0 : claimsRecommendationOverturnReasons.Cancel) || action === 'Approve' && (claimsRecommendationOverturnReasons === null || claimsRecommendationOverturnReasons === void 0 ? void 0 : claimsRecommendationOverturnReasons.Approve) || null;
  const handleUpdateClaim = async variables => {
    var _data$claimAgent3, _data$claimAgent3$cla, _result$data, _claim, _claim2, _claim3;
    let actionReason = null;
    if (data !== null && data !== void 0 && (_data$claimAgent3 = data.claimAgent) !== null && _data$claimAgent3 !== void 0 && (_data$claimAgent3$cla = _data$claimAgent3.claim) !== null && _data$claimAgent3$cla !== void 0 && _data$claimAgent3$cla.recommendedAction) {
      var _overturnReasonOption;
      const overturnReasonOptions = overturnReasonOptionsForAction(data.claimAgent.claim.recommendedAction);
      actionReason = (overturnReasonOptions === null || overturnReasonOptions === void 0 ? void 0 : (_overturnReasonOption = overturnReasonOptions.find(_ref4 => {
        let {
          value
        } = _ref4;
        return value === variables.actionReason;
      })) === null || _overturnReasonOption === void 0 ? void 0 : _overturnReasonOption.label) ?? variables.actionReason;
    }
    const result = await updateClaim({
      variables: {
        action: variables.action,
        actionReason,
        localeCountry,
        localeLanguage,
        timezone,
        retailerMoniker,
        id
      }
    });
    const {
      success
    } = ((_result$data = result.data) === null || _result$data === void 0 ? void 0 : _result$data.updateClaimAgent) ?? {};
    titan === null || titan === void 0 ? void 0 : titan.track({
      event: 'order_completed',
      properties: {
        products: [{
          product_id: id,
          category: (_claim = claim) === null || _claim === void 0 ? void 0 : _claim.claimType,
          variant: (_claim2 = claim) === null || _claim2 === void 0 ? void 0 : _claim2.status
        }],
        extra_properties: [{
          key: 'request_result',
          value: success ? 'SUCCESS' : 'FAILURE'
        }, {
          key: 'request_error_message',
          value: success ? undefined : updateClaimError === null || updateClaimError === void 0 ? void 0 : updateClaimError.message
        }, {
          key: 'overturn_reason',
          value: actionReason
        }, {
          key: 'review_action',
          value: variables.action
        }, {
          key: 'recommended_review_action',
          value: (_claim3 = claim) === null || _claim3 === void 0 ? void 0 : _claim3.recommendedAction
        }]
      }
    })
    // eslint-disable-next-line no-console
    .catch(e => console.error(e));
    if (success) {
      setUpdateClaimSuccess(true);
    }
  };
  return /*#__PURE__*/React__default["default"].createElement(ClaimDetailsContext.Provider, {
    value: {
      id,
      orderNumber,
      claim,
      claimDetailsLoading,
      claimDetailsError: !!claimDetailsError,
      handleUpdateClaim,
      updateClaimLoading,
      updateClaimError: !!updateClaimError || (updateClaimData === null || updateClaimData === void 0 ? void 0 : (_updateClaimData$upda = updateClaimData.updateClaimAgent) === null || _updateClaimData$upda === void 0 ? void 0 : _updateClaimData$upda.success) === false,
      overturnReasonOptionsForAction,
      updateClaimSuccess
    }
  }, children);
};

exports.ClaimDetailsContext = ClaimDetailsContext;
exports.ClaimDetailsProvider = ClaimDetailsProvider;
exports.useClaimDetails = useClaimDetails;
